import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Email from "@mapmycustomers/shared/types/email/Email";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/map/types";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import EmailCreationProvider from "@app/component/email/creation/CreateEmailProvider";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";

import { getChildEmails, getEmailThreadsEntity, isChildEmailsLoading } from "../store/selectors";
import useParentEmailThreadsFolderAnalytics from "../util/useParentEmailThreadsFolderAnalytics";

import ChildEmail from "./ChildEmail";
import styles from "./ChildEmailsList.module.scss";
import NewEmail from "./NewEmail";

interface ChildEmailsListProps {
  emails: Email[];
  emailsLoading: boolean;
  entity: EntitiesSupportedByEmailFeature;
}

const ChildEmailsList: React.FC<ChildEmailsListProps> = ({ emails, emailsLoading, entity }) => {
  const analytics = useAnalytics();

  const emailsAnalytics = useExtendedAnalytics(
    "Email Thread",
    useParentEmailThreadsFolderAnalytics()
  );
  const newEmailAnalytics = useExtendedAnalytics("Email Thread", analytics);

  const [collapsed, collapse, expand] = useBoolean(true);
  const [replyParams, setReplyParams] = useState<{
    email: Email;
    repliedAll: Email["repliedAll"];
  }>();

  const expandableEmailThreadsCount = Math.max(0, emails.length - 2);

  const displayEmailThreads = useMemo(() => {
    if (collapsed) {
      return [emails[0], emails[emails.length - 1]].filter(isDefined);
    }
    return emails;
  }, [emails, collapsed]);

  useEffect(() => {
    if (!emails.length) {
      return;
    }
    if (expandableEmailThreadsCount) {
      collapse();
    } else {
      expand();
    }
  }, [emails, expandableEmailThreadsCount, collapse, expand]);

  const handleReply = useCallback(
    (email: Email, repliedAll: boolean) => {
      setReplyParams({ email, repliedAll });
      newEmailAnalytics.clicked([repliedAll ? "Reply All" : "Reply"]);
    },
    [newEmailAnalytics, setReplyParams]
  );

  const handleNewEmailClose = useCallback(() => {
    setReplyParams(undefined);
  }, [setReplyParams]);

  return (
    <div className={styles.container}>
      <AnalyticsContext.Provider value={emailsAnalytics}>
        {displayEmailThreads.map((email, index) => [
          <ChildEmail
            email={email}
            initialExpanded={index === displayEmailThreads.length - 1}
            key={`emailThread${email.id}`}
            onReply={(repliedAll) => handleReply(email, repliedAll)}
          />,
          collapsed && index === 0 ? (
            <div className={styles.toggleContainer} key="emailThreadsToggler">
              <div className={styles.toggleButtonContainer}>
                <Button className={styles.toggleButton} onClick={expand} type="text">
                  {expandableEmailThreadsCount}
                </Button>
              </div>
            </div>
          ) : null,
        ])}
      </AnalyticsContext.Provider>
      {replyParams && (
        <AnalyticsContext.Provider value={newEmailAnalytics}>
          <EmailCreationProvider
            initialValues={{ recipients: [entity], recipientType: entity.entity }}
          >
            <NewEmail
              onClose={handleNewEmailClose}
              repliedAll={replyParams.repliedAll}
              replyEmail={replyParams.email}
            />
          </EmailCreationProvider>
        </AnalyticsContext.Provider>
      )}
      {emailsLoading && <LoadingSpinner className={styles.loadingSpinner} />}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  emails: getChildEmails(state),
  emailsLoading: isChildEmailsLoading(state),
  entity: getEmailThreadsEntity(state)!,
});

export default connect(mapStateToProps)(ChildEmailsList);
